import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@shared/lib/supabase';
import AdminAuditLog from '../components/AdminAuditLog';

interface Stats {
  pendingApps: number;
  totalMerchants: number;
  totalOrders: number;
  totalVolume: number;
  completedToday: number;
  failedToday: number;
}

function StatCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      className="rounded p-4 transition-colors"
      style={{
        border: highlight ? '1px solid rgba(245,158,11,0.2)' : '1px solid var(--dash-border)',
        background: highlight ? 'rgba(245,158,11,0.05)' : 'var(--dash-card)',
      }}
      onMouseEnter={(e) => { if (!highlight) e.currentTarget.style.background = 'var(--dash-card-hover)'; }}
      onMouseLeave={(e) => { if (!highlight) e.currentTarget.style.background = 'var(--dash-card)'; }}
    >
      <div
        className="font-mono text-[10px] uppercase tracking-widest mb-1.5"
        style={{ color: highlight ? undefined : 'var(--dash-text-dim)' }}
      >
        {highlight ? <span className="text-amber-400">{label}</span> : label}
      </div>
      <div
        className="font-mono text-xl"
        style={{ color: highlight ? undefined : 'var(--dash-text-primary)' }}
      >
        {highlight ? <span className="text-amber-300">{value}</span> : value}
      </div>
    </div>
  );
}

function HealthIndicator({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-red-400'}`} />
      <span
        className="font-sans text-xs"
        style={{ color: ok ? 'var(--dash-text-tertiary)' : undefined }}
      >
        {ok ? label : <span className="text-red-400">{label}</span>}
      </span>
    </div>
  );
}

export default function AdminOverview() {
  const [stats, setStats] = useState<Stats>({
    pendingApps: 0,
    totalMerchants: 0,
    totalOrders: 0,
    totalVolume: 0,
    completedToday: 0,
    failedToday: 0,
  });
  const [recentApps, setRecentApps] = useState<
    { id: string; business_name: string; email: string; status: string; created_at: string }[]
  >([]);
  const [recentOrders, setRecentOrders] = useState<
    { id: string; expected_amount: number; status: string; created_at: string; merchants: { business_name: string } | null }[]
  >([]);
  const [dbHealthy, setDbHealthy] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const [appsCount, merchantsCount, ordersRes, recentAppsRes, recentOrdersRes, todayCompletedRes, todayFailedRes] =
      await Promise.all([
        supabase
          .from('merchant_applications')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'pending'),
        supabase.from('merchants').select('id', { count: 'exact', head: true }),
        supabase.from('orders').select('id, verified_amount, status'),
        supabase
          .from('merchant_applications')
          .select('id, business_name, email, status, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('orders')
          .select('id, expected_amount, status, created_at, merchants(business_name)')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'completed')
          .gte('created_at', todayStart.toISOString()),
        supabase
          .from('orders')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'failed')
          .gte('created_at', todayStart.toISOString()),
      ]);

    const totalVolume =
      ordersRes.data
        ?.filter((o) => o.status === 'completed')
        .reduce((sum, o) => sum + (Number(o.verified_amount) || 0), 0) || 0;

    setDbHealthy(!ordersRes.error);

    setStats({
      pendingApps: appsCount.count || 0,
      totalMerchants: merchantsCount.count || 0,
      totalOrders: ordersRes.data?.length || 0,
      totalVolume,
      completedToday: todayCompletedRes.count || 0,
      failedToday: todayFailedRes.count || 0,
    });
    setRecentApps((recentAppsRes.data as typeof recentApps) || []);
    setRecentOrders((recentOrdersRes.data as typeof recentOrders) || []);
    setLoading(false);
  };

  const statusColor = (s: string) => {
    if (s === 'pending') return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    if (s === 'approved' || s === 'completed') return 'text-green-400 bg-green-400/10 border-green-400/20';
    if (s === 'rejected' || s === 'failed') return 'text-red-400 bg-red-400/10 border-red-400/20';
    if (s === 'expired') return 'text-zinc-200 bg-zinc-800/60 border-zinc-700/50';
    return 'text-zinc-400 bg-zinc-800/30 border-zinc-700/30';
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <p className="font-mono text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Admin Overview</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="Pending Apps"
          value={String(stats.pendingApps)}
          highlight={stats.pendingApps > 0}
        />
        <StatCard label="Merchants" value={String(stats.totalMerchants)} />
        <StatCard label="Total Orders" value={String(stats.totalOrders)} />
        <StatCard
          label="Volume"
          value={`$${stats.totalVolume.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        />
        <StatCard label="Completed Today" value={String(stats.completedToday)} />
        <StatCard
          label="Failed Today"
          value={String(stats.failedToday)}
          highlight={stats.failedToday > 0}
        />
      </div>

      <div
        className="rounded p-5 mb-6"
        style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
      >
        <div className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-dim)' }}>
          System Health
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <HealthIndicator label="Database" ok={dbHealthy} />
          <HealthIndicator label="Supabase Auth" ok={true} />
          <HealthIndicator label="Edge Functions" ok={true} />
          <HealthIndicator label="Realtime" ok={true} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
              Recent Applications
            </h2>
            <Link
              to="/04062001/admin/applications"
              className="font-mono text-xs transition-colors flex items-center gap-1"
              style={{ color: 'var(--dash-text-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
            >
              View all <span className="text-[11px]">{'\u2192'}</span>
            </Link>
          </div>
          {recentApps.length === 0 ? (
            <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No applications yet.</p>
            </div>
          ) : (
            <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
              {recentApps.map((app, i) => (
                <div
                  key={app.id}
                  className="px-4 py-3 flex items-center justify-between"
                  style={{
                    background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                    borderBottom: i < recentApps.length - 1 ? '1px solid var(--dash-border-light)' : undefined,
                  }}
                >
                  <div>
                    <p className="font-sans text-sm" style={{ color: 'var(--dash-text-secondary)' }}>{app.business_name}</p>
                    <p className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>{app.email}</p>
                  </div>
                  <span
                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${statusColor(app.status)}`}
                  >
                    {app.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
              Recent Orders
            </h2>
            <Link
              to="/04062001/admin/orders"
              className="font-mono text-xs transition-colors flex items-center gap-1"
              style={{ color: 'var(--dash-text-muted)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
            >
              View all <span className="text-[11px]">{'\u2192'}</span>
            </Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
              <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>No orders yet.</p>
            </div>
          ) : (
            <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
              {recentOrders.map((order, i) => (
                <div
                  key={order.id}
                  className="px-4 py-3 flex items-center justify-between"
                  style={{
                    background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                    borderBottom: i < recentOrders.length - 1 ? '1px solid var(--dash-border-light)' : undefined,
                  }}
                >
                  <div>
                    <p className="font-mono text-xs" style={{ color: 'var(--dash-text-secondary)' }}>
                      {order.id.slice(0, 8)}...
                    </p>
                    <p className="font-mono text-[10px]" style={{ color: 'var(--dash-text-dim)' }}>
                      {order.merchants?.business_name || 'Unknown'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xs" style={{ color: 'var(--dash-text-secondary)' }}>
                      ${Number(order.expected_amount).toFixed(2)}
                    </p>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-widest ${statusColor(order.status)}`}
                    >
                      {order.status === 'expired' ? 'abandoned' : order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="font-mono text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--dash-text-dim)' }}>
          Recent Activity
        </h2>
        <AdminAuditLog limit={10} compact />
      </div>
    </div>
  );
}
